import { BaseService } from "@/services/base-service";
import { API_BASE_URL } from "@/config";
import { CANCEL_STRINGS_PATH, CancellationReason, isHopCancellable } from "./cancellation.types";
import { Order } from "@/services/orders/orders.types";
import { getString } from "../../common/uistringlabels/ui-string-utils";

const ITO_PROXY_PREFIX = "/ito-amznham";
const TERMINAL_STATUSES = ["CANCELLED", "COMPLETED", "DELIVERED"];

export interface CancellationResult {
  success: boolean;
  error?: string;
}

const encodeGreedyPath = (taxonomyId: string): string =>
  taxonomyId.split("/").map(encodeURIComponent).join("/");

class CancellationService extends BaseService {
  private apiBase: BaseService;

  constructor() {
    super(API_BASE_URL + ITO_PROXY_PREFIX);
    this.apiBase = new BaseService(API_BASE_URL);
  }

  public async cancelOrder(orderId: string, reason: CancellationReason): Promise<CancellationResult> {
    try {
      const tokenResponse = await this.apiBase.fetch("/csrf");
      if (!tokenResponse.ok) throw new Error("Failed to get CSRF token");
      const { token: csrfToken } = await tokenResponse.json();

      const encodedOrderId = encodeGreedyPath(orderId);

      const orderResponse = await this.fetch(`/order/${encodedOrderId}`);
      if (!orderResponse.ok) throw new Error("Failed to fetch order");
      const order: Order = await orderResponse.json();

      if (!isHopCancellable(order.items)) {
        return {
          success: false,
          error: getString(`${CANCEL_STRINGS_PATH}.progressedError`),
        };
      }

      const currentDetails = (order as unknown as Record<string, unknown>).details ?? {};
      const statusResponse = await this.fetch(`/order/${encodedOrderId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
        body: JSON.stringify({ status: "CANCELLED", details: { ...currentDetails, cancellationReason: reason } }),
      });
      if (!statusResponse.ok) throw new Error("Failed to update order status");

      const items = order.items ?? [];
      for (const item of items) {
        if (item.status && TERMINAL_STATUSES.includes(item.status.toUpperCase())) continue;

        try {
          await this.fetch(`/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(item.itemId)}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
            body: JSON.stringify({ status: "CANCELLED" }),
          });
        } catch {
          return {
            success: false,
            error: getString(`${CANCEL_STRINGS_PATH}.partialItemsError`),
          };
        }
      }

      return { success: true };
    } catch {
      return { success: false };
    }
  }
}

export const cancellationService = new CancellationService();
